import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Typography,
  MenuItem,
} from "@mui/material";
import ApiServices from "../../../Services/ApiServices/ApiServices";

const EmployeesEdit = ({ employee = null, onSuccess = () => {}, onCancel = () => {} }) => {
  const [businesses, setBusinesses] = useState([]);
  const [business, setBusiness] = useState(employee?.business?.id ?? "");
  const [firstName, setFirstName] = useState(employee?.first_name ?? "");
  const [lastName, setLastName] = useState(employee?.last_name ?? "");
  const [email, setEmail] = useState(employee?.email ?? "");
  const [phone, setPhone] = useState(employee?.phone ?? "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingBusinesses, setLoadingBusinesses] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "error" });

  useEffect(() => {
    fetchBusinesses();
    // populate when employee changes
    setBusiness(employee?.business?.id ?? "");
    setFirstName(employee?.first_name ?? "");
    setLastName(employee?.last_name ?? "");
    setEmail(employee?.email ?? "");
    setPhone(employee?.phone ?? "");
    setPassword("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee]);

  const fetchBusinesses = async () => {
    setLoadingBusinesses(true);
    try {
      const res = await ApiServices.get("external-businesses/");
      const data = res?.data ?? res;
      setBusinesses(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setSnack({ open: true, message: "Failed to load businesses", severity: "error" });
    } finally {
      setLoadingBusinesses(false);
    }
  };

  const closeSnack = () => setSnack((s) => ({ ...s, open: false }));

  const validate = () => {
    if (!employee) {
      setSnack({ open: true, message: "No employee selected", severity: "error" });
      return false;
    }
    if (!business) {
      setSnack({ open: true, message: "Please select a business", severity: "error" });
      return false;
    }
    if (!firstName.trim()) {
      setSnack({ open: true, message: "First name required", severity: "error" });
      return false;
    }
    if (!lastName.trim()) {
      setSnack({ open: true, message: "Last name required", severity: "error" });
      return false;
    }
    if (!email.trim()) {
      setSnack({ open: true, message: "Email required", severity: "error" });
      return false;
    }
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRe.test(email)) {
      setSnack({ open: true, message: "Enter a valid email", severity: "error" });
      return false;
    }
    if (!phone.trim() || !/^\d{7,15}$/.test(phone.replace(/\D/g, ""))) {
      setSnack({ open: true, message: "Enter a valid phone (7-15 digits)", severity: "error" });
      return false;
    }
    // password required for edit (as requested)
    if (!password || password.length < 6) {
      setSnack({ open: true, message: "Password required (min 6 chars) to update", severity: "error" });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e && e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const payload = {
        business: Number(business),
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim(),
        phone: phone.trim(),
        password,
      };
      const res = await ApiServices.put(`external-employees/${employee.id}/`, payload);
      const data = res?.data ?? res;
      setSnack({ open: true, message: "Employee updated", severity: "success" });
      onSuccess("Employee updated", data);
    } catch (err) {
      console.error(err);
      let msg = "Failed to update employee.";
      if (err?.response?.data) {
        const d = err.response.data;
        if (typeof d === "string") msg = d;
        else if (d?.detail) msg = d.detail;
        else msg = Object.values(d).flat().join(" ") || msg;
      } else if (err?.message) msg = err.message;
      setSnack({ open: true, message: msg, severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
      <Typography sx={{ color: "#094c9f", fontWeight: 700 }}>Edit Employee</Typography>

      <TextField
        select
        label={loadingBusinesses ? "Loading businesses..." : "Business"}
        value={business}
        onChange={(e) => setBusiness(e.target.value)}
        required
        fullWidth
        helperText="Select business for this employee"
      >
        {businesses.map((b) => (
          <MenuItem key={b.id} value={b.id}>
            {b.name}
          </MenuItem>
        ))}
      </TextField>

      <TextField label="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} required fullWidth />
      <TextField label="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} required fullWidth />
      <TextField label="Email" value={email} onChange={(e) => setEmail(e.target.value)} required fullWidth />
      <TextField label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} required fullWidth />
      <TextField
        label="Password (required)"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        required
        type="password"
        fullWidth
      />

      <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", pt: 1 }}>
        <Button onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button type="submit" variant="contained" disabled={loading} sx={{ backgroundColor: "#094c9f" }}>
          {loading ? <CircularProgress size={20} /> : "Save Changes"}
        </Button>
      </Box>

      <Snackbar open={snack.open} autoHideDuration={4200} onClose={closeSnack}>
        <Alert onClose={closeSnack} severity={snack.severity} sx={{ width: "100%" }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EmployeesEdit;
